import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpInterceptorParams } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { AuthService } from '../authentication/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  #authService: AuthService = null;
  readonly #csrfToken: string = crypto.randomUUID();
  readonly #tabstarted: string;

  constructor() {
    const tabStartDate = new Date();
    this.#tabstarted = `${tabStartDate.getTime()}#${tabStartDate.toISOString()}`;
  }

  public setAuthService(authService: AuthService): void {
    this.#authService = authService;
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    req = req.clone({
      withCredentials: true,
    });

    if (req.url.indexOf('/api') <= -1) {
      // no api request, nothing to do
      return next.handle(req);
    }

    if (req.url.endsWith('/api/oauth2/token')) {
      return this.#authService.getAuthToken().pipe(
        switchMap((idToken) => {
          if (idToken) {
            req = req.clone({
              setHeaders: {
                'X-GB-Authorization': idToken,
              },
            });
            return next.handle(req);
          } else {
            return next.handle(req);
          }
        }),
      );
    }

    // generate xsrf token
    document.cookie = `XSRF-TOKEN=${this.#csrfToken};path=/`;
    req = req.clone({
      headers: req.headers.set('X-XSRF-TOKEN', this.#csrfToken).set('X-TABID', this.#tabstarted),
    });

    if (environment.apihost_ba && !req.url.endsWith('/apic/exante')) {
      req = req.clone({
        headers: req.headers.set('authorization', environment.apihost_ba),
      });
    }

    if (req.url.endsWith('/api/auth/save') || req.url.endsWith('/api/auth/sync') || req.url.endsWith('/api/auth/logout')) {
      return next.handle(req);
    }

    if (
      req.url.endsWith(environment.apiLoginUrl) ||
      req.url.endsWith('/startOnboarding') ||
      req.url.endsWith('/signupuser') ||
      req.url.endsWith('/registrationconfirmation') ||
      req.url.endsWith('/resendConfirmationMail') ||
      req.url.endsWith('/api/instrument/emissions') ||
      req.url.endsWith(environment.tokenRefreshUrl)
    ) {
      return next.handle(req); // gibt den Request einfach weiter
    }

    // Ab hier muss ein Authservice vorhanden sein sonst wäre es ein fehler
    if (!this.#authService) {
      throw new Error('Authservice fehlt!');
    }

    // getAuth() muss asynchron sein, ansonsten kriegen wir 401 bevor Amplify das token
    // refreshed hat! Mit mergeMap warten wir quasi drauf bevor das nächste handle aufgerufen wird.

    return this.#authService.getAuthToken().pipe(
      mergeMap((idToken) => {
        if (
          req.url.indexOf('/apic/') > -1 ||
          req.url.indexOf('/apis/') > -1 ||
          req.url.indexOf('/api/instrument-cache/') > -1 ||
          environment.sendAuthHeaderForApi
        ) {
          // for requests to lamda functions we provide the token in a header
          if (idToken) {
            req = req.clone({
              // withCredentials: false,
              headers: req.headers.set('X-GB-Authorization', idToken),
            });
          }
        }

        // Ausserdem muss auch noch das Cookie mit dem Token am Server angekommen sein und das cookie gesetzt sein!
        return next.handle(req).pipe(
          catchError((err: HttpErrorResponse) => {
            const errorMessageFromServer = err.error?.message ? err.error.message : undefined;
            if (err.status === 401 || err.status === 403) {
              if (!req.url.endsWith('api/auth/logout')) {
                if (err.error && err.error.status === 'LOCKED') {
                  this.#authService.logout(false, true, errorMessageFromServer, {
                    reason: 'request err.status',
                    errStatus: err.status,
                    errErrorStatus: err.error.status,
                  });
                } else {
                  this.#authService.logout(false, true, undefined, {
                    reason: 'request err.status',
                    errStatus: err.status,
                  });
                }
              }
            } else if (err.status >= 400 && err.status !== 503) {
              const { params } = req;
              params instanceof HttpInterceptorParams &&
                params.interceptorConfig.skipInterceptorErrorHandler === true &&
                typeof params.interceptorConfig.useErrorHandler === 'function' &&
                params.interceptorConfig.useErrorHandler(err);
            }
            return throwError(() => err);
          }),
        );
      }),
    );
  }
}
